'use client';

import { useState, useEffect, useCallback } from 'react';
import { getEmbedBridge } from '../lib/embed-bridge';

interface Props {
  productId: string;
  selections: Record<string, any>;
}

interface PriceData {
  currency: string;
  basePrice: number;
  subtotal: number;
  tax: number;
  taxRate: number;
  total: number;
  formatted: string;
  breakdown: { label: string; amount: number; operation: string }[];
}

export function PricingDisplay({ productId, selections }: Props) {
  const [pricing, setPricing] = useState<PriceData | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [cartAdded, setCartAdded] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    fetch(`/api/pricing/${productId}`)
      .then((r) => r.json())
      .then((data) => { if (data.showPricing) setEnabled(true); })
      .catch(() => {});
  }, [productId]);

  const calculate = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await fetch(`/api/pricing/${productId}/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selections }),
      });
      const data = await res.json();
      setPricing(data);
    } catch {}
  }, [productId, selections, enabled]);

  useEffect(() => { calculate(); }, [calculate]);

  if (!enabled || !pricing) return null;

  const handleAddToCart = useCallback(async () => {
    try {
      const res = await fetch(`/api/pricing/${productId}/cart-payload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selections, quantity: 1 }),
      });
      const payload = await res.json();

      getEmbedBridge().emit('c3d:addToCart', payload);

      const event = new CustomEvent('c3d:addToCart', { detail: payload });
      window.dispatchEvent(event);

      setCartAdded(true);
      setTimeout(() => setCartAdded(false), 2000);
    } catch {}
  }, [productId, selections]);

  const sym = pricing.formatted.replace(/[\d.,]/g, '').trim() || '$';

  return (
    <div className="border-t border-zinc-200 dark:border-zinc-800 pt-3 mt-3">
      <div className="flex items-end justify-between mb-2">
        <div>
          <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Total Price</p>
          <p className="text-2xl font-bold tracking-tight">{pricing.formatted}</p>
        </div>
        <button
          onClick={() => setShowBreakdown(!showBreakdown)}
          className="text-[10px] text-blue-500 hover:text-blue-600"
        >
          {showBreakdown ? 'Hide details' : 'View breakdown'}
        </button>
      </div>

      {showBreakdown && (
        <div className="mb-3 rounded-lg bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 overflow-hidden">
          <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {pricing.breakdown.map((item, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                <span className="text-zinc-500">{item.label}</span>
                <span className={`font-medium ${item.operation === 'subtract' ? 'text-green-600' : ''}`}>
                  {item.operation === 'add' ? '+' : item.operation === 'subtract' ? '-' : ''}
                  {sym}{Math.abs(item.amount).toFixed(2)}
                </span>
              </div>
            ))}
            {pricing.taxRate > 0 && (
              <div className="flex items-center justify-between px-3 py-1.5 text-xs">
                <span className="text-zinc-500">Tax ({pricing.taxRate}%)</span>
                <span className="font-medium">+{sym}{pricing.tax.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between px-3 py-2 text-sm font-semibold bg-zinc-100 dark:bg-zinc-800">
              <span>Total</span>
              <span>{pricing.formatted}</span>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handleAddToCart}
        disabled={cartAdded}
        className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all ${
          cartAdded
            ? 'bg-green-500 text-white'
            : 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 hover:opacity-90'
        }`}
      >
        {cartAdded ? '✓ Added to Cart' : `Add to Cart — ${pricing.formatted}`}
      </button>
    </div>
  );
}
